/* Theme wishlist — Worker-backed.
 *
 * Replaces the prior localStorage-only module. Public API surface is
 * unchanged so existing markup ([data-heart][data-product-id]) keeps
 * working without theme changes; the storage backend moved to the
 * yuki-backend Worker via App Proxy at /apps/api/wishlist/*.
 *
 *   window.ThemeWishlist.has(productId)       -> boolean (sync; reads cache)
 *   window.ThemeWishlist.toggle(productId, opts)  -> Promise<{liked, like_count}>
 *   window.ThemeWishlist.add(productId, opts)     -> Promise<{liked, like_count}>
 *   window.ThemeWishlist.remove(productId)        -> Promise<{liked, like_count}>
 *   window.ThemeWishlist.list()                   -> Promise<Array<item>>
 *   window.ThemeWishlist.count()                  -> Promise<number>
 *   window.ThemeWishlist.subscribe(fn)            -> unsubscribe
 *
 * Internally:
 *   - A local Set caches wished product IDs for instant `has()` checks
 *     (used by every heart on the page for visual state).
 *   - On page load, /apps/api/wishlist/check?ids=... batch-hydrates the
 *     cache for hearts visible on the page.
 *   - Mutations call /apps/api/wishlist/add or /remove with optimistic UI
 *     + rollback on network error.
 *   - Like counts on cards re-render from the response.
 *
 * Auth headers come from window.ThemeWishlistSession.getAuthHeaders().
 * Logged-in customers ride the App Proxy (no header); guests send
 * X-Wishlist-Session-Token. See assets/wishlist-session.js.
 */
(function () {
  if (window.__themeWishlistInit) return;
  window.__themeWishlistInit = true;

  // ── Cache ─────────────────────────────────────────────────────────
  // Set of String(productId) → wished. Cleared on login migration so
  // we re-hydrate from the customer's server-side state. The cache is
  // a synchronous-read shadow of the server; mutations reconcile after
  // the network call returns.
  var cache = new Set();
  var subscribers = new Set();

  // ── Pending requests dedupe ───────────────────────────────────────
  // 200ms debounce per-product to avoid spamming the Worker on rapid
  // clicks. The spec's "Slow down" rate limit is also enforced
  // server-side at 30/min per session/customer.
  var inFlight = new Map();   // productId → Promise
  var pendingTimers = new Map();

  function api() { return (window.ThemeWishlistSession || {}); }
  function apiBase() { return api().getApiBase ? api().getApiBase() : '/apps/api/wishlist'; }
  function authHeaders() { return api().getAuthHeaders ? api().getAuthHeaders() : { 'Content-Type': 'application/json' }; }

  function notify() {
    var snapshot = Array.from(cache);
    subscribers.forEach(function (fn) { try { fn(snapshot); } catch (e) { /* ignore subscriber errors */ } });
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: snapshot, count: snapshot.length } }));
  }

  function setLiked(productId, liked) {
    var sid = String(productId);
    var was = cache.has(sid);
    if (liked) cache.add(sid); else cache.delete(sid);
    if (was !== liked) notify();
  }

  // ── Hydration from /mine ─────────────────────────────────────────
  // Always populate the cache from the FULL wishlist (not just IDs
  // visible on this page) so the header badge reflects the true total
  // even when the wished products aren't on the current page.
  //
  // Then refresh per-product like counts for the hearts that ARE
  // visible (cheap separate call against the public /counts endpoint).
  async function hydrateFromPage() {
    // ── 1. Full wishlist → cache (always, on every page load) ────
    try {
      var mineResp = await fetch(apiBase() + '/mine', {
        method: 'GET',
        headers: authHeaders(),
      });
      if (mineResp.ok) {
        var mineData = await mineResp.json();
        cache.clear();
        for (var i = 0; i < (mineData.items || []).length; i++) {
          var item = mineData.items[i];
          if (item && item.shopify_product_id != null) {
            cache.add(String(item.shopify_product_id));
          }
        }
        notify();
      }
    } catch (err) {
      // Silent — hearts default to unwished, user can still click to wish.
    }

    // ── 2. Refresh per-product like counts for visible hearts ────
    var hearts = document.querySelectorAll('[data-heart][data-product-id]');
    if (hearts.length === 0) return;
    var ids = [];
    hearts.forEach(function (h) {
      var id = h.dataset.productId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.length === 0) return;
    try {
      var countResp = await fetch(apiBase() + '/counts?ids=' + ids.join(','), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!countResp.ok) return;
      var countData = await countResp.json();
      var countsMap = countData.counts || {};
      hearts.forEach(function (h) {
        var c = countsMap[h.dataset.productId];
        if (typeof c === 'number') {
          // Add the per-product "starting boost" set in the Featured
          // tab (Darphane). The boost is published into the
          // collection-index file as a separate `b` field + emitted
          // into `data-boost` on this button by the snippet. Without
          // adding it here, the Worker's live-only count would
          // overwrite the boosted number that the snippet rendered
          // initially — boost would visibly vanish on hydrate.
          var boost = parseInt(h.dataset.boost || '0', 10) || 0;
          var displayed = c + boost;
          var el = h.querySelector('[data-heart-count]');
          if (el) el.textContent = formatCount(displayed);
          h.dataset.likeCount = String(displayed);
        }
      });
    } catch (err) { /* ignore */ }
  }

  // ── K-abbreviated count formatter ─────────────────────────────────
  // Per spec: 0-999 exact, 1000-9999 "1.2K", 10000+ "12K".
  function formatCount(n) {
    if (!Number.isFinite(n)) return '0';
    if (n < 1000) return String(n);
    if (n < 10000) return (Math.round(n / 100) / 10).toFixed(1).replace(/\.0$/, '') + 'K';
    return Math.round(n / 1000) + 'K';
  }

  // ── Mutations ─────────────────────────────────────────────────────

  /** Add a product to the wishlist. `opts` carries the product snapshot
   *  (handle/title/image_url/format_label) and variant context. */
  async function add(productId, opts) {
    return mutate(productId, 'add', opts || {});
  }
  async function remove(productId) {
    return mutate(productId, 'remove', {});
  }
  async function toggle(productId, opts) {
    return mutate(productId, has(productId) ? 'remove' : 'add', opts || {});
  }
  function has(productId) { return cache.has(String(productId)); }
  function listSync() { return Array.from(cache); }

  async function mutate(productId, action, opts, rollbackState) {
    var sid = String(productId);
    // Dedupe: if a request for the same (id) is in flight, return that.
    if (inFlight.has(sid)) return inFlight.get(sid);

    var body = {
      shopify_product_id: parseInt(sid, 10),
    };
    if (action === 'add') {
      body.product_handle = opts.product_handle || '';
      body.product_title = opts.product_title || '';
      body.product_image_url = opts.product_image_url || '';
      if (opts.product_format_label) body.product_format_label = opts.product_format_label;
      if (opts.selected_format) body.selected_format = opts.selected_format;
      if (opts.selected_size) body.selected_size = opts.selected_size;
      if (opts.selected_frame_color) body.selected_frame_color = opts.selected_frame_color;
    }

    // Optimistic UI: when the caller (click handler) already flipped
    // the cache, it passes `rollbackState` = pre-flip value so the
    // error path restores correctly. When called from the public
    // API (add/remove/toggle), no flip has happened yet → we do it
    // here and the rollback target is the pre-call cache state.
    var optimisticLiked = action === 'add';
    var hadCached = rollbackState !== undefined ? rollbackState : cache.has(sid);
    if (rollbackState === undefined) {
      setLiked(productId, optimisticLiked);
    }

    var promise = fetch(apiBase() + '/' + action, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    })
      .then(function (resp) {
        if (!resp.ok) {
          return resp.json().catch(function () { return {}; }).then(function (errBody) {
            // Roll back optimistic update.
            setLiked(productId, hadCached);
            var err = new Error(errBody.error || ('HTTP ' + resp.status));
            err.code = errBody.error;
            err.status = resp.status;
            err.detail = errBody.detail;
            throw err;
          });
        }
        return resp.json();
      })
      .then(function (data) {
        // Reconcile with server response.
        setLiked(productId, !!data.liked);
        // Update like count on the heart that was just toggled.
        // Same boost-add as the hydration path above — Worker's
        // data.like_count is live-only; we add data-boost so the
        // displayed number stays consistent with what the merchant
        // configured in the Featured tab.
        document.querySelectorAll('[data-heart][data-product-id="' + sid + '"]').forEach(function (h) {
          if (typeof data.like_count !== 'number') return;
          var boost = parseInt(h.dataset.boost || '0', 10) || 0;
          var displayed = data.like_count + boost;
          var el = h.querySelector('[data-heart-count]');
          if (el) el.textContent = formatCount(displayed);
          h.dataset.likeCount = String(displayed);
        });
        return data;
      })
      .finally(function () {
        inFlight.delete(sid);
      });

    inFlight.set(sid, promise);
    return promise;
  }

  // ── Async list/count (network) ────────────────────────────────────
  async function listAsync() {
    try {
      var resp = await fetch(apiBase() + '/mine', { method: 'GET', headers: authHeaders() });
      if (!resp.ok) return [];
      var data = await resp.json();
      return data.items || [];
    } catch (err) {
      return [];
    }
  }

  async function countAsync() {
    var items = await listAsync();
    return items.length;
  }

  // ── Click handler for all [data-heart][data-product-id] elements ──
  function onClick(e) {
    var btn = e.target.closest && e.target.closest('[data-heart][data-product-id]');

    // Defensive fallback for Dawn's stretched-link cards: the title
    // anchor's `::after` overlays the entire card to make any click on
    // the card navigate to the product page. Even with the heart at
    // z-index: 5 the overlay sometimes wins because the heart's
    // stacking context differs (Dawn's `.card__inner.ratio` doesn't
    // always promote children above absolutely-positioned siblings of
    // ancestors). When that happens, `e.target` is the `<a>` (or
    // really nothing in the heart subtree), and `closest` returns null.
    //
    // `elementsFromPoint` (plural — returns ALL elements at the point
    // in painting order) lets us look UNDER the visual top-most
    // element and recover the heart if it's actually at the click
    // position. Bulletproof against any z-index / stacking quirk.
    if (!btn && typeof document.elementsFromPoint === 'function') {
      var stack = document.elementsFromPoint(e.clientX, e.clientY);
      for (var i = 0; i < stack.length; i++) {
        var hit = stack[i].closest && stack[i].closest('[data-heart][data-product-id]');
        if (hit) { btn = hit; break; }
      }
    }

    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    var productId = btn.dataset.productId;
    // Debounce: 200ms per-button. If a user mashes the heart, we
    // collapse the final state into one network call.
    if (pendingTimers.has(productId)) {
      clearTimeout(pendingTimers.get(productId));
    }
    // Capture the pre-click state ONCE per click burst so the network
    // rollback can restore exactly what was there before the user
    // touched the button. We stash it on the button's dataset because
    // the timer fires asynchronously and the closure variable would
    // be lost if the user mashes the heart 5×.
    if (btn.dataset._wishlistInitialState === undefined) {
      btn.dataset._wishlistInitialState = has(productId) ? 'true' : 'false';
    }
    var initialLiked = btn.dataset._wishlistInitialState === 'true';
    var nextLiked = !has(productId);
    setLiked(productId, nextLiked);   // immediate visual flip

    pendingTimers.set(productId, setTimeout(function () {
      pendingTimers.delete(productId);
      var finalLiked = has(productId);
      // Net-zero: even number of clicks → cache state same as before.
      // Skip the network call entirely.
      if (finalLiked === initialLiked) {
        delete btn.dataset._wishlistInitialState;
        return;
      }
      delete btn.dataset._wishlistInitialState;

      var opts = {
        product_handle: btn.dataset.productHandle || '',
        product_title: btn.dataset.productTitle || '',
        product_image_url: btn.dataset.productImage || '',
        product_format_label: btn.dataset.productFormatLabel || undefined,
        selected_format: btn.dataset.selectedFormat || undefined,
        selected_size: btn.dataset.selectedSize || undefined,
        selected_frame_color: btn.dataset.selectedFrameColor || undefined,
      };
      var action = finalLiked ? 'add' : 'remove';
      // Pass `initialLiked` as the rollback target so a 404 / network
      // error reverts the heart to its true pre-click state, not the
      // post-flip state.
      mutate(productId, action, opts, initialLiked).catch(function (err) {
        if (err.code === 'guest_cap_reached') {
          showToast(err.detail || 'Wishlist full — sign up to save unlimited favorites.', 'warn');
        } else {
          var status = err.status ? ' (' + err.status + ')' : '';
          showToast('Could not update wishlist' + status + '. Please try again.', 'error');
        }
      });
    }, 200));
  }

  // ── Visual state sync ─────────────────────────────────────────────
  function applyState() {
    document.querySelectorAll('[data-heart][data-product-id]').forEach(function (btn) {
      var isOn = has(btn.dataset.productId);
      btn.classList.toggle('is-active', isOn);
      btn.classList.toggle('liked', isOn);
      btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      var n = cache.size;
      el.textContent = String(n);
      el.toggleAttribute('hidden', n === 0);
    });
  }

  // ── Toast ─────────────────────────────────────────────────────────
  function showToast(message, kind) {
    var existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.className = 'wishlist-toast wishlist-toast--' + (kind || 'info');
    t.textContent = message;
    t.style.cssText = [
      'position:fixed', 'bottom:24px', 'left:50%', 'transform:translateX(-50%)',
      'background:' + (kind === 'error' ? '#dc2626' : kind === 'warn' ? '#d97706' : '#111827'),
      'color:#fff', 'padding:10px 16px', 'border-radius:8px',
      'font-size:13px', 'z-index:9999', 'box-shadow:0 4px 12px rgba(0,0,0,0.18)',
      'max-width:90vw', 'text-align:center',
    ].join(';');
    document.body.appendChild(t);
    setTimeout(function () { t.remove(); }, 4500);
  }

  // ── Public API ────────────────────────────────────────────────────
  var pub = {
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    list: listAsync,
    listSync: listSync,
    count: countAsync,
    countSync: function () { return cache.size; },
    formatCount: formatCount,
    subscribe: function (fn) { subscribers.add(fn); return function () { subscribers.delete(fn); }; },
    refreshFromPage: hydrateFromPage,
  };
  window.ThemeWishlist = pub;

  // Re-sync visual state whenever the cache changes.
  pub.subscribe(applyState);

  // Click handler in CAPTURE phase so it fires BEFORE any bubble-phase
  // navigation handlers. Dawn's `.card__heading a::after` stretched
  // overlay turns the entire card into a clickable link to the product
  // page — even with the heart at z-index: 2, the browser's link-click
  // dispatch races our handler in bubble phase. Capture phase guarantees
  // ours runs first so preventDefault() actually cancels the navigation.
  document.addEventListener('click', onClick, true);
  document.addEventListener('shopify:section:load', hydrateFromPage);
  document.addEventListener('wishlist:migrated', function () {
    // Cache is stale — re-hydrate under the new customer auth.
    cache.clear();
    notify();
    hydrateFromPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateFromPage);
  } else {
    hydrateFromPage();
  }
})();
